import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HelpPage = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('client');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { feeRates } = useFees();
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/company-info`).then(r => setCompanyInfo(r.data)).catch(() => {});
  }, []);

  const providerNet = (1000 * (1 - feeRates.provider_fee_rate / 100)).toLocaleString();
  const fmtFaqs = (list) => list.map((f) => ({
    question: f.q,
    answer: (f.a || '')
      .replace(/\{\{clientFee\}\}/g, feeRates.client_fee_rate)
      .replace(/\{\{providerFee\}\}/g, feeRates.provider_fee_rate)
      .replace(/\{\{providerNet\}\}/g, providerNet),
  }));
  const clientFaqs = fmtFaqs(t('helpFaqs.client', { returnObjects: true }));
  const providerFaqs = fmtFaqs(t('helpFaqs.provider', { returnObjects: true }));
  const generalFaqs = fmtFaqs(t('helpFaqs.general', { returnObjects: true }));

  const currentFaqs = activeTab === 'client' ? clientFaqs : activeTab === 'provider' ? providerFaqs : generalFaqs;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="help-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-4">
            {t('help.title')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('help.subtitle')}
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <a href="#faq" className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="text-4xl mb-2">
              <svg className="w-10 h-10 mx-auto text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="font-bold text-gray-800">{t('help.faqsTitle')}</h3>
            <p className="text-sm text-gray-500">{t('help.faqsSub')}</p>
          </a>
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('open-support-chat', { detail: { view: 'contactOptions' } }));
            }}
            className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition"
            data-testid="help-contact-support"
          >
            <div className="text-4xl mb-2">
              <svg className="w-10 h-10 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            </div>
            <h3 className="font-bold text-gray-800">{t('help.contactCardTitle')}</h3>
            <p className="text-sm text-gray-500">{t('help.contactCardSub')}</p>
          </button>
          <a href="/account-settings" className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="text-4xl mb-2">
              <svg className="w-10 h-10 mx-auto text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <h3 className="font-bold text-gray-800">{t('help.settingsCardTitle')}</h3>
            <p className="text-sm text-gray-500">{t('help.settingsCardSub')}</p>
          </a>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('client')}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === 'client'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="client-faq-tab"
            >
              👤 {t('help.forClients')}
            </button>
            <button
              onClick={() => setActiveTab('provider')}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === 'provider'
                  ? 'bg-green-50 text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="provider-faq-tab"
            >
              🛠️ {t('help.forProviders')}
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 py-4 px-6 font-semibold transition ${
                activeTab === 'general'
                  ? 'bg-purple-50 text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="general-faq-tab"
            >
              📌 {t('help.general')}
            </button>
          </div>

          {/* FAQ List */}
          <div className="p-6">
            <div className="space-y-4">
              {currentFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="border rounded-lg overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full text-left px-6 py-4 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
                  >
                    <span className="font-semibold text-gray-800">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 py-4 bg-white border-t">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{t('help.stillNeedHelp')}</h2>
          <p className="mb-6 opacity-90">
            {t('help.contactSupport')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-support-chat', { detail: { view: 'newTicket' } }));
              }}
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
              data-testid="help-send-message"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              {t('help.sendMessage')}
            </button>
            <button
              onClick={() => {
                if (companyInfo.contact_email && companyInfo.show_email !== false) {
                  window.location.href = `mailto:${companyInfo.contact_email}`;
                } else {
                  window.dispatchEvent(new CustomEvent('open-support-chat', { detail: { view: 'contactOptions' } }));
                }
              }}
              className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-30 transition border border-white border-opacity-50"
              data-testid="help-email-support"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              {t('help.emailSupport')}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HelpPage;
